import { Fragment } from 'react';
import { Modal, Tag, Typography, Button, Empty } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { formatCents } from '@/utils/format';
import { thumb } from '@/utils/image';

interface ResourceItem {
  id: number;
  imageId: number;
  label: string | null;
  image: { accessUrl: string };
}

interface ParamRow {
  key: string;
  label: string;
  value: unknown;
}

interface ModelSummary {
  slug: string;
  displayName: string;
  params: ParamRow[];
  rmbCents: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  runLabel: string;
  prompt: string;
  referenceResources: ResourceItem[];
  userInputResources: ResourceItem[];
  models: ModelSummary[];
  totalRmbCents: number;
}

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 11,
  color: '#94A3B8',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  marginBottom: 8,
};

const RequestPreviewModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  runLabel,
  prompt,
  referenceResources,
  userInputResources,
  models,
  totalRmbCents,
}) => {
  const hasRef = referenceResources.length > 0;
  const hasUser = userInputResources.length > 0;
  const hasPrompt = prompt.trim().length > 0;
  const hasModels = models.length > 0;
  const isEmpty = !hasRef && !hasUser && !hasPrompt && !hasModels;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      title={
        <span>
          <ThunderboltOutlined style={{ color: '#D97706', marginRight: 6 }} />
          请求预览
          {runLabel && (
            <Tag color="gold" style={{ marginLeft: 8 }}>
              {runLabel}
            </Tag>
          )}
        </span>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            点击「生成」后会立即创建 {models.length || 0} 条待执行记录，可关闭此窗口继续别的操作
          </Typography.Text>
          <div>
            <Button onClick={onClose}>关闭</Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              disabled={!hasModels}
              loading={submitting}
              onClick={onSubmit}
              style={{ marginLeft: 8 }}
            >
              {submitting ? '提交中' : `生成 · ${formatCents(totalRmbCents)}`}
            </Button>
          </div>
        </div>
      }
    >
      {isEmpty ? (
        <Empty
          className="brand-empty"
          description="还没有配置任何输入 — 在左边添加源图、指令并勾选模型"
          style={{ padding: 40 }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FEF9EC 100%)',
            border: '1px solid #F0EBDE',
            borderRadius: 12,
            padding: '16px 18px',
          }}
        >
          {hasRef && (
            <Section label={`参考图 · ${referenceResources.length}`}>
              <ThumbStrip items={referenceResources} />
            </Section>
          )}

          {hasUser && (
            <Section label={`用户输入 · ${userInputResources.length}`}>
              <ThumbStrip items={userInputResources} />
            </Section>
          )}

          {hasPrompt && (
            <Section label="指令">
              <div
                style={{
                  background: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  color: '#92400E',
                  padding: '8px 12px',
                  borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {prompt}
              </div>
            </Section>
          )}

          <Section label={`模型 · ${models.length}`}>
            {hasModels ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {models.map((m) => (
                  <ModelCard key={m.slug} model={m} />
                ))}
              </div>
            ) : (
              <div style={{ color: '#94A3B8', fontSize: 13 }}>（请至少勾选一个模型）</div>
            )}
          </Section>

          {hasModels && (
            <Section label="合计">
              <div style={{ fontSize: 18, fontWeight: 600, color: '#D97706' }}>
                {formatCents(totalRmbCents)}
              </div>
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={SECTION_LABEL}>{label}</div>
    {children}
  </div>
);

const ThumbStrip: React.FC<{ items: ResourceItem[] }> = ({ items }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {items.map((r) => (
      <div
        key={r.id}
        title={r.label ?? undefined}
        style={{
          width: 56,
          height: 56,
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #EEE7D4',
          background: '#F6F3EC',
          flexShrink: 0,
        }}
      >
        <img
          src={thumb(r.image.accessUrl, 80)}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    ))}
  </div>
);

const ModelCard: React.FC<{ model: ModelSummary }> = ({ model }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #EEE7D4',
      borderRadius: 10,
      padding: '10px 12px',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
            color: '#92400E',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          ⚡
        </div>
        <div
          style={{
            fontWeight: 500,
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {model.displayName}
        </div>
      </div>
      <Tag color="gold" style={{ margin: 0, flexShrink: 0 }}>
        {formatCents(model.rmbCents)}
      </Tag>
    </div>
    {model.params.length > 0 ? (
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px dashed #F0EBDE',
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 12,
          rowGap: 4,
          fontSize: 12,
        }}
      >
        {model.params.map((p) => (
          <Fragment key={p.key}>
            <div style={{ color: '#94A3B8' }}>{p.label}</div>
            <div style={{ color: '#334155' }}>{formatParamValue(p.value)}</div>
          </Fragment>
        ))}
      </div>
    ) : (
      <div style={{ marginTop: 6, fontSize: 12, color: '#94A3B8' }}>（默认参数）</div>
    )}
  </div>
);

function formatParamValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default RequestPreviewModal;
